import { useEffect, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRHub } from '@/realtime/useSignalRHub';
import type { NopUpdatePayload, OverviewUpdatePayload } from '@/api/types/loginOverview';

interface Options {
  enabled: boolean;
  login: number;
  connector: number;
  toRc: boolean;
  onOverview: (p: OverviewUpdatePayload) => void;
  onNop: (p: NopUpdatePayload) => void;
}

// Login Overview live: two hubs (overview + nop), each Subscribe(login, connector,
// toRcConverted) on connect, and re-subscribe (unsub old + sub new) when the
// login/connector/RC changes without a reconnect.
export function useLoginOverviewLive({ enabled, login, connector, toRc, onOverview, onNop }: Options) {
  const argsRef = useRef({ login, connector, toRc });
  argsRef.current = { login, connector, toRc };
  const onOverviewRef = useRef(onOverview);
  onOverviewRef.current = onOverview;
  const onNopRef = useRef(onNop);
  onNopRef.current = onNop;

  const ov = useSignalRHub({
    url: '/hubs/login-overview',
    enabled,
    on: { OverviewUpdate: (p) => onOverviewRef.current(p as OverviewUpdatePayload) },
    onConnected: (c) => {
      const a = argsRef.current;
      void c.invoke('Subscribe', a.login, a.connector, a.toRc).catch(() => {});
    },
  });

  const nop = useSignalRHub({
    url: '/hubs/login-overview-nop',
    enabled,
    on: { NopUpdate: (p) => onNopRef.current(p as NopUpdatePayload) },
    onConnected: (c) => {
      const a = argsRef.current;
      void c.invoke('Subscribe', a.login, a.connector, a.toRc).catch(() => {});
    },
  });

  const prevRef = useRef({ login, connector, toRc });
  useEffect(() => {
    const prev = prevRef.current;
    prevRef.current = { login, connector, toRc };
    if (prev.login === login && prev.connector === connector && prev.toRc === toRc) return;
    for (const c of [ov.connection.current, nop.connection.current]) {
      if (c && c.state === HubConnectionState.Connected) {
        void c.invoke('Unsubscribe', prev.login, prev.connector, prev.toRc).catch(() => {});
        void c.invoke('Subscribe', login, connector, toRc).catch(() => {});
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [login, connector, toRc]);

  return { overviewStatus: ov.status, nopStatus: nop.status };
}
